import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Bell,
  CalendarDays,
  CheckCircle2,
  CircleStop,
  CreditCard,
  Download,
  ExternalLink,
  LogIn,
  LogOut,
  MessageSquare,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  ThumbsDown,
  ThumbsUp
} from "lucide-react";
import { api, AuthState, BillingState, JobState, Lead, NicheProfile, OperatorEventSnapshot } from "./api";

const buyerReasons = [
  "explicit_purchase_intent",
  "comparison_shopping",
  "switching_intent",
  "pain_point",
  "research_phase",
  "implicit_product_need",
  "weak_precursor"
];

const noiseReasons = [
  "noise_promotional",
  "noise_support",
  "noise_discussion",
  "noise_content"
];

type TimeWindowMode = "3d" | "week" | "month" | "custom";

const timeWindowChoices: { mode: TimeWindowMode; label: string; days: number }[] = [
  { mode: "3d", label: "Last 3 days", days: 3 },
  { mode: "week", label: "Week", days: 7 },
  { mode: "month", label: "Month", days: 30 },
  { mode: "custom", label: "Custom", days: 7 }
];

function canUseWorkspace(auth: AuthState | null | undefined) {
  return Boolean(auth?.isAuthenticated || auth?.apiAuthRequired === false);
}

function defaultContactMessage(keyword: string) {
  const topic = keyword?.trim();
  return topic
    ? `I'd like private beta access to search for "${topic}" leads (and more keywords). Please let me know how to get set up.`
    : "I'd like private beta access to run more lead searches. Please let me know how to get set up.";
}

export function App() {
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [authLoaded, setAuthLoaded] = useState(false);
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [profiles, setProfiles] = useState<NicheProfile[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<number>>(new Set());
  const [ratedLeads, setRatedLeads] = useState<Record<number, "up" | "down">>({});
  // Monotonic id for the current search; bumping it cancels any pending follow-up dig.
  const searchRunRef = useRef(0);
  const [statusFilter, setStatusFilter] = useState("new");
  const [gradeFilter, setGradeFilter] = useState("");
  const [query, setQuery] = useState("");
  const [timeWindowMode, setTimeWindowMode] = useState<TimeWindowMode>("week");
  const [customStartDate, setCustomStartDate] = useState(defaultCustomStartDate);
  const [customEndDate, setCustomEndDate] = useState(todayInputDate);
  const [lastSubmittedQuery, setLastSubmittedQuery] = useState("");
  const [lastSubmittedWindowLabel, setLastSubmittedWindowLabel] = useState("");
  const [liveFetch, setLiveFetch] = useState<JobState | null>(null);
  const [sourceDiscovery, setSourceDiscovery] = useState<JobState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previousResultsKeyword, setPreviousResultsKeyword] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [operatorEvents, setOperatorEvents] = useState<OperatorEventSnapshot | null>(null);
  const [operatorPanelOpen, setOperatorPanelOpen] = useState(false);
  const [operatorSeenAt, setOperatorSeenAt] = useState(
    () => window.localStorage.getItem("demandsift.operator-events-seen-at") ?? ""
  );

  const hasWorkspaceAccess = canUseWorkspace(auth);
  const hasBillingAccess = billing?.hasAccess ?? true;
  const running = Boolean(sourceDiscovery?.isRunning || liveFetch?.isRunning);
  const liveFetchKeyword = typeof liveFetch?.searchKeyword === "string" ? liveFetch.searchKeyword : "";
  const liveFetchMatchesCurrentSearch =
    Boolean(lastSubmittedQuery) &&
    normalize(liveFetchKeyword) === normalize(lastSubmittedQuery);
  const latestScanSince =
    liveFetchMatchesCurrentSearch &&
    typeof liveFetch?.runStartedAt === "string"
      ? (liveFetch.runStartedAt as string)
      : undefined;
  const hasLeadContext = Boolean(latestScanSince || previousResultsKeyword);
  const discoveredSources = useMemo(
    () => {
      const pursueSources = readStringArray(sourceDiscovery ?? {}, "pursueSources");
      const candidateSources = readStringArray(sourceDiscovery ?? {}, "candidateSources");
      return pursueSources.length > 0 ? pursueSources : candidateSources.slice(0, 12);
    },
    [sourceDiscovery]
  );
  const selectedLeads = useMemo(
    () => leads.filter((lead) => selectedLeadIds.has(lead.id)),
    [leads, selectedLeadIds]
  );
  const allVisibleSelected = leads.length > 0 && leads.every((lead) => selectedLeadIds.has(lead.id));
  const unseenOperatorEvents = useMemo(() => {
    const seen = operatorSeenAt ? Date.parse(operatorSeenAt) : 0;
    return (operatorEvents?.events ?? []).filter((event) => Date.parse(event.timestamp) > seen).length;
  }, [operatorEvents, operatorSeenAt]);
  const selectedWindow = useMemo(
    () => resolveTimeWindow(timeWindowMode, customStartDate, customEndDate),
    [timeWindowMode, customStartDate, customEndDate]
  );

  async function loadStaticState() {
    const user = await api.currentUser();
    setAuth(user);
    setAuthLoaded(true);

    if (!canUseWorkspace(user)) {
      setBilling(null);
      setLeads([]);
      return user;
    }

    const [billingState, niches, operations] = await Promise.allSettled([
      api.billing(),
      api.niches(),
      user.isOperationsAdmin ? api.operatorHealthEvents() : Promise.resolve(null)
    ]);
    if (billingState.status === "fulfilled") setBilling(billingState.value);
    if (niches.status === "fulfilled") setProfiles(niches.value.profiles);
    if (operations.status === "fulfilled" && operations.value) setOperatorEvents(operations.value);
    return user;
  }

  async function loadLeads() {
    if (!hasWorkspaceAccess || !hasLeadContext) {
      setLeads([]);
      return;
    }

    const rows = await api.leads({
      status: statusFilter,
      grade: gradeFilter,
      seenAfter: latestScanSince,
      keyword: latestScanSince ? undefined : previousResultsKeyword,
      sort: "priority",
      limit: 150
    });
    // Customer view = qualified demand only. Hide weak C/D grades unless the grade
    // filter explicitly asks for them.
    const showLowGrades = gradeFilter === "C" || gradeFilter === "D";
    const visible = showLowGrades
      ? rows
      : rows.filter((lead) => {
          const g = (lead.leadGrade || "").toUpperCase();
          return g !== "C" && g !== "D";
        });
    setLeads(visible);
    setSelectedLeadIds((current) => {
      const visibleIds = new Set(visible.map((lead) => lead.id));
      return new Set([...current].filter((id) => visibleIds.has(id)));
    });
  }

  async function pollJobs(currentAuth: AuthState | null = auth) {
    if (!canUseWorkspace(currentAuth)) return;
    const [live, discovery] = await Promise.allSettled([
      api.liveFetch(),
      api.sourceDiscovery()
    ]);
    if (live.status === "fulfilled") setLiveFetch(live.value);
    if (discovery.status === "fulfilled") setSourceDiscovery(discovery.value);
  }

  useEffect(() => {
    loadStaticState()
      .then((user) => pollJobs(user))
      .catch((err) => {
        setAuthLoaded(true);
        setError(err.message);
      });
  }, []);

  // On a fresh page load (e.g. a browser refresh) the in-memory search context is
  // lost, so the lead list would render empty even though a scan just ran or is
  // running. Restore the context from the backend's last/active scan once, so the
  // latest leads + progress show on every refresh.
  const restoredContextRef = useRef(false);
  useEffect(() => {
    if (restoredContextRef.current || lastSubmittedQuery) return;
    const kw = typeof liveFetch?.searchKeyword === "string" ? liveFetch.searchKeyword.trim() : "";
    if (!kw) return;
    restoredContextRef.current = true;
    setLastSubmittedQuery(kw);
    setPreviousResultsKeyword(kw);
    setQuery((current) => current || kw);
  }, [liveFetch, lastSubmittedQuery]);

  useEffect(() => {
    if (!hasWorkspaceAccess) return;
    const timer = window.setInterval(() => {
      pollJobs().catch((err) => setError(err.message));
      loadLeads().catch((err) => setError(err.message));
    }, running ? 1000 : 5000);
    return () => window.clearInterval(timer);
  }, [hasWorkspaceAccess, hasLeadContext, latestScanSince, previousResultsKeyword, statusFilter, gradeFilter, running]);

  useEffect(() => {
    loadLeads().catch((err) => setError(err.message));
  }, [latestScanSince, previousResultsKeyword, statusFilter, gradeFilter]);

  useEffect(() => {
    if (!auth?.isOperationsAdmin) {
      setOperatorEvents(null);
      setOperatorPanelOpen(false);
      return;
    }
    const refresh = () => api.operatorHealthEvents().then(setOperatorEvents).catch(() => undefined);
    refresh();
    const timer = window.setInterval(refresh, 30_000);
    return () => window.clearInterval(timer);
  }, [auth?.isOperationsAdmin]);

  function toggleOperatorPanel() {
    const opening = !operatorPanelOpen;
    setOperatorPanelOpen(opening);
    if (opening) {
      const latest = operatorEvents?.events[0]?.timestamp ?? new Date().toISOString();
      window.localStorage.setItem("demandsift.operator-events-seen-at", latest);
      setOperatorSeenAt(latest);
    }
  }

  async function findLeads(event?: FormEvent) {
    event?.preventDefault();
    const keyword = query.trim();
    if (!keyword) return;
    const searchWindow = resolveTimeWindow(timeWindowMode, customStartDate, customEndDate);
    if (searchWindow.error) {
      setError(searchWindow.error);
      return;
    }
    if (!hasBillingAccess) {
      setError(billing?.message ?? "An active subscription is required.");
      return;
    }

    setError(null);
    setLeads([]);
    setSelectedLeadIds(new Set());
    setPreviousResultsKeyword("");
    setContactSent(false);
    setLastSubmittedQuery(keyword);
    setLastSubmittedWindowLabel(searchWindow.label);

    const matchedProfile = bestProfileForQuery(keyword, profiles);
    const includeProfileSeeds = Boolean(matchedProfile);
    if (matchedProfile) {
      await api.setActiveNiche(matchedProfile.name).catch(() => undefined);
    }

    setSourceDiscovery({
      isRunning: true,
      isComplete: false,
      step: "Finding buyer communities",
      logs: [],
      pursueSources: [],
      candidateSources: []
    });

    // Discovery and the scan run IN PARALLEL: the scan's fast lane surfaces
    // first leads from the exact keyword (+ profile seeds when matched) while
    // discovery explores for buyer communities. A follow-up dig then covers any
    // newly discovered sources. Combined request rate stays within Arctic
    // Shift's documented comfort zone (single-threaded fetchers, 1.5s delays).
    searchRunRef.current += 1;
    const runId = searchRunRef.current;

    try {
      const started = await api.startSourceDiscovery(
        keyword,
        searchWindow.daysBack,
        25,
        60,
        searchWindow.startDate,
        searchWindow.endDate,
        includeProfileSeeds
      );
      setSourceDiscovery(started);
      if (started.alreadySearched) {
        setLiveFetch({
          isRunning: false,
          isComplete: true,
          searchKeyword: keyword,
          alreadySearched: true,
          message: started.message
        });
        setPreviousResultsKeyword(keyword);
        setContactOpen(true);
        setContactMessage(defaultContactMessage(keyword));
        return;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const lowerMessage = message.toLowerCase();
      if (lowerMessage.includes("keyword search limit") || lowerMessage.includes("one keyword search")) {
        setError(message);
        setContactOpen(true);
        setContactMessage(defaultContactMessage(keyword));
        setSourceDiscovery((current) => ({
          ...(current ?? {}),
          isRunning: false,
          isComplete: false,
          lastError: message
        }));
        return;
      }
      // Discovery is an accelerator, not a prerequisite — scan proceeds without it.
    }

    setLiveFetch({
      isRunning: true,
      isComplete: false,
      step: "Reading recent posts and comments",
      logs: [],
      newLeadsFound: 0,
      insertedLeadCount: 0,
      updatedLeadCount: 0,
      persistedLeadCount: 0
    });

    try {
      const scanState = await api.startLiveFetch({
        daysBack: searchWindow.daysBack,
        sources: [],
        keyword,
        startDate: searchWindow.startDate,
        endDate: searchWindow.endDate,
        includeProfileSeeds,
        contextTerms: [],
        searchPhrases: []
      });
      setLiveFetch(scanState);
      if (scanState.alreadySearched) {
        setPreviousResultsKeyword(keyword);
        setContactOpen(true);
        setContactMessage(defaultContactMessage(keyword));
        return;
      }
      window.setTimeout(() => pollJobs().catch((err) => setError(err.message)), 750);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await pollJobs().catch(() => undefined);
      return;
    }

    // Background: once discovery and the first scan both finish, dig into any
    // sources discovery found that the scan didn't already cover.
    followUpDig(runId, keyword, searchWindow, includeProfileSeeds).catch((err) =>
      setError(err instanceof Error ? err.message : String(err))
    );
  }

  async function followUpDig(
    runId: number,
    keyword: string,
    searchWindow: { daysBack: number; startDate?: string; endDate?: string },
    includeProfileSeeds: boolean
  ) {
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    // Wait for discovery (bounded, tolerant of transient poll errors).
    let pursue: string[] = [];
    let contextTerms: string[] = [];
    let searchPhrases: string[] = [];
    const discoveryDeadline = Date.now() + 5 * 60 * 1000;
    while (Date.now() < discoveryDeadline) {
      await sleep(2000);
      if (searchRunRef.current !== runId) return; // superseded or stopped
      try {
        const state = await api.sourceDiscovery();
        setSourceDiscovery(state);
        if (!state?.isRunning) {
          const pursueSources = readStringArray(state, "pursueSources");
          const candidateSources = readStringArray(state, "candidateSources");
          contextTerms = readStringArray(state, "contextTerms");
          searchPhrases = readStringArray(state, "searchPhrases");
          pursue = pursueSources.length > 0 ? pursueSources : candidateSources.slice(0, 12);
          break;
        }
      } catch {
        // transient poll failure — keep waiting
      }
    }
    if (pursue.length === 0) return;

    // Wait for the first scan to finish and note which sources it covered.
    let covered: string[] = [];
    const scanDeadline = Date.now() + 30 * 60 * 1000;
    while (Date.now() < scanDeadline) {
      if (searchRunRef.current !== runId) return;
      try {
        const state = await api.liveFetch();
        if (!state?.isRunning) {
          covered = readStringArray(state, "sourcesUsed");
          break;
        }
      } catch {
        // transient poll failure — keep waiting
      }
      await sleep(3000);
    }

    const coveredSet = new Set(covered.map((s) => s.toLowerCase()));
    const newSources = pursue.filter((s) => !coveredSet.has(s.toLowerCase()));
    if (newSources.length === 0 || searchRunRef.current !== runId) return;

    // Follow-up dig: newly discovered sources only, same full quality path.
    const digState = await api.startLiveFetch({
      daysBack: searchWindow.daysBack,
      sources: newSources,
      keyword,
      startDate: searchWindow.startDate,
      endDate: searchWindow.endDate,
      includeProfileSeeds: false,
      contextTerms,
      searchPhrases
    });
    if (!digState.alreadySearched) {
      setLiveFetch(digState);
      window.setTimeout(() => pollJobs().catch((err) => setError(err.message)), 750);
    }
  }

  async function stopSearch() {
    setError(null);
    searchRunRef.current += 1; // cancel any pending follow-up dig
    const stops: Promise<JobState>[] = [];
    if (sourceDiscovery?.isRunning) stops.push(api.stopSourceDiscovery());
    if (liveFetch?.isRunning) stops.push(api.stopLiveFetch());
    await Promise.allSettled(stops);
    await pollJobs();
  }

  async function markLead(lead: Lead, label: "buyer" | "not_buyer", reason: string) {
    const status = label === "buyer" ? "contacted" : "dismissed";
    await api.updateLead(lead.id, { status, label, reason, source: "human" });
    await loadLeads();
  }

  async function rateLead(lead: Lead, verdict: "up" | "down") {
    setRatedLeads((current) => ({ ...current, [lead.id]: verdict }));
    try {
      await api.setLeadPreference(lead.id, verdict);
      await loadLeads();
    } catch (err) {
      setRatedLeads((current) => {
        const next = { ...current };
        delete next[lead.id];
        return next;
      });
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  function toggleLeadSelection(leadId: number, selected: boolean) {
    setSelectedLeadIds((current) => {
      const next = new Set(current);
      if (selected) next.add(leadId);
      else next.delete(leadId);
      return next;
    });
  }

  function toggleVisibleLeads(selected: boolean) {
    setSelectedLeadIds((current) => {
      const next = new Set(current);
      for (const lead of leads) {
        if (selected) next.add(lead.id);
        else next.delete(lead.id);
      }
      return next;
    });
  }

  function exportSelected(format: "csv" | "xls" | "pdf") {
    if (!hasBillingAccess) {
      setError(billing?.message ?? "An active subscription is required.");
      return;
    }
    if (selectedLeads.length === 0) {
      setError("Select at least one lead to export.");
      return;
    }

    if (format === "csv") exportRowsCsv(selectedLeads, lastSubmittedQuery);
    if (format === "xls") exportRowsXls(selectedLeads, lastSubmittedQuery);
    if (format === "pdf") exportRowsPdf(selectedLeads, lastSubmittedQuery);
  }

  async function startCheckout(plan: string) {
    setError(null);
    try {
      const response = await api.startCheckout(plan);
      window.location.assign(response.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function openBillingPortal() {
    setError(null);
    try {
      const response = await api.billingPortal();
      window.location.assign(response.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function signOut() {
    setError(null);
    await api.logout();
    const nextAuth = await api.currentUser();
    setAuth(nextAuth);
    setBilling(null);
    setLeads([]);
    setSelectedLeadIds(new Set());
    setLiveFetch(null);
    setSourceDiscovery(null);
    setLastSubmittedQuery("");
    setLastSubmittedWindowLabel("");
  }

  if (!authLoaded) {
    return (
      <main className="app-shell compact-shell">
        <section className="auth-panel">
          <div className="brand-lockup">
            <span className="brand-mark">DS</span>
            <span>DemandSift</span>
          </div>
          <h1>Loading your demand workspace</h1>
        </section>
      </main>
    );
  }

  if (!hasWorkspaceAccess) {
    return (
      <AuthGate
        googleEnabled={auth?.googleEnabled ?? false}
        error={error}
        onDismissError={() => setError(null)}
      />
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <span className="brand-mark">DS</span>
          <span>DemandSift</span>
        </div>
        <div className="status-row">
          {auth?.isOperationsAdmin && (
            <button
              className="icon-button operator-bell"
              onClick={toggleOperatorPanel}
              title="Operations alerts"
              aria-label="Operations alerts"
              aria-expanded={operatorPanelOpen}
            >
              <Bell size={18} />
              {unseenOperatorEvents > 0 && (
                <span className="operator-alert-count">{Math.min(unseenOperatorEvents, 99)}</span>
              )}
            </button>
          )}
          <button className="icon-button" onClick={() => loadStaticState()} title="Refresh">
            <RefreshCw size={18} />
          </button>
          {auth?.isAuthenticated && (
            <button className="icon-button" onClick={() => signOut().catch((err) => setError(err.message))} title="Sign out">
              <LogOut size={18} />
            </button>
          )}
        </div>
      </header>

      {auth?.isOperationsAdmin && operatorPanelOpen && (
        <section className="operator-events" aria-label="Operations alerts">
          <div className="operator-events-heading">
            <div>
              <p className="eyebrow">Private operations</p>
              <h2>Pipeline alerts</h2>
            </div>
            <span>
              Checked {operatorEvents?.lastCheckedAt ? formatOperatorTime(operatorEvents.lastCheckedAt) : "not yet"}
            </span>
          </div>
          {(operatorEvents?.events.length ?? 0) === 0 ? (
            <p className="operator-events-empty">No degraded-path alerts are recorded.</p>
          ) : (
            <div className="operator-event-list">
              {operatorEvents?.events.slice(0, 10).map((event, index) => (
                <div className={`operator-event ${event.severity}`} key={`${event.timestamp}-${event.service}-${index}`}>
                  <AlertTriangle size={16} />
                  <div>
                    <strong>{event.service}</strong>
                    <p>{event.message}</p>
                  </div>
                  <time dateTime={event.timestamp}>{formatOperatorTime(event.timestamp)}</time>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {error && (
        <section className="notice error">
          <CircleStop size={18} />
          <span>{shortError(error)}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </section>
      )}

      <BillingNotice
        billing={billing}
        onCheckout={(plan) => startCheckout(plan)}
        onPortal={openBillingPortal}
      />

      <section className="search-panel">
        <form className="lead-search-form" onSubmit={findLeads}>
          <Search size={22} />
          <input
            aria-label="Lead search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="What Product/Service Leads would you like?"
            autoFocus
          />
          {running ? (
            <button type="button" className="stop-button" onClick={() => stopSearch().catch((err) => setError(err.message))}>
              <CircleStop size={17} />
              Stop
            </button>
          ) : (
            <button className="primary" disabled={!hasBillingAccess || !query.trim()} type="submit">
              Find Leads
            </button>
          )}
        </form>

        <TimeWindowPicker
          mode={timeWindowMode}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          selectedWindow={selectedWindow}
          disabled={running}
          onModeChange={setTimeWindowMode}
          onStartDateChange={setCustomStartDate}
          onEndDateChange={setCustomEndDate}
        />

        {running ? (
          <ProgressExperience
            query={lastSubmittedQuery}
            windowLabel={lastSubmittedWindowLabel}
            liveFetch={liveFetch}
            sourceDiscovery={sourceDiscovery}
            discoveredSources={discoveredSources}
          />
        ) : null}
      </section>

      {latestScanSince && (
        <section className="lead-toolbar">
          <div className="toolbar-title">
            <Sparkles size={18} />
            <span>{leads.length} opportunit{leads.length === 1 ? "y" : "ies"} ready</span>
          </div>
          <div className="filters">
            <label className="select-filter">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                disabled={leads.length === 0}
                onChange={(event) => toggleVisibleLeads(event.target.checked)}
              />
              Select all
            </label>
            <span className="selected-count">{selectedLeads.length} selected</span>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">Any status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="dismissed">Dismissed</option>
            </select>
            <select value={gradeFilter} onChange={(event) => setGradeFilter(event.target.value)}>
              <option value="">Qualified (A/B)</option>
              <option value="A">A opportunities</option>
              <option value="B">B opportunities</option>
              <option value="C">C opportunities</option>
              <option value="D">D opportunities</option>
            </select>
            <button onClick={() => loadLeads()}>
              <RefreshCw size={15} />
              Refresh
            </button>
            <button disabled={!hasBillingAccess || selectedLeads.length === 0} onClick={() => exportSelected("csv")} title="Export selected CSV">
              <Download size={15} />
              CSV
            </button>
            <button disabled={!hasBillingAccess || selectedLeads.length === 0} onClick={() => exportSelected("xls")} title="Export selected Excel file">
              <Download size={15} />
              XLS
            </button>
            <button disabled={!hasBillingAccess || selectedLeads.length === 0} onClick={() => exportSelected("pdf")} title="Export selected PDF">
              <Download size={15} />
              PDF
            </button>
          </div>
        </section>
      )}

      <section className="lead-results" aria-busy={running}>
        {leads.map((lead) => (
          <LeadCard
            key={lead.id}
            lead={lead}
            selected={selectedLeadIds.has(lead.id)}
            rated={ratedLeads[lead.id]}
            onToggleSelected={toggleLeadSelection}
            onMark={markLead}
            onRate={rateLead}
          />
        ))}
        {!running && latestScanSince && leads.length === 0 && (
          <div className="empty">
            <ShieldCheck size={28} />
            <span>No fresh qualified opportunities are visible for this run yet.</span>
          </div>
        )}
      </section>
    </main>
  );
}

function ProgressExperience({
  query,
  windowLabel,
  liveFetch,
  sourceDiscovery,
  discoveredSources
}: {
  query: string;
  windowLabel: string;
  liveFetch: JobState | null;
  sourceDiscovery: JobState | null;
  discoveredSources: string[];
}) {
  const found = readNumber(liveFetch ?? {}, "newLeadsFound");
  const sources = discoveredSources.slice(0, 6);

  return (
    <div className="progress-experience">
      <div className="progress-copy">
        <p className="eyebrow">{[query || "Your search", windowLabel].filter(Boolean).join(" · ")}</p>
        <h2>Qualifying demand signals</h2>
        <p>{customerProgressLine(sourceDiscovery, liveFetch)}</p>
      </div>
      <div className="calm-progress" aria-hidden="true">
        <span />
      </div>
      <div className="progress-facts">
        <span>
          <Search size={15} />
          Buyer communities
        </span>
        <span>
          <MessageSquare size={15} />
          Customer conversations
        </span>
        <span>
          <CheckCircle2 size={15} />
          {found > 0 ? `${found} candidates` : "High-intent filtering"}
        </span>
      </div>
      {sources.length > 0 && (
        <div className="source-chips">
          {sources.map((source) => (
            <span key={source}>r/{source}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function TimeWindowPicker({
  mode,
  customStartDate,
  customEndDate,
  selectedWindow,
  disabled,
  onModeChange,
  onStartDateChange,
  onEndDateChange
}: {
  mode: TimeWindowMode;
  customStartDate: string;
  customEndDate: string;
  selectedWindow: SearchWindow;
  disabled: boolean;
  onModeChange: (mode: TimeWindowMode) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
}) {
  const today = todayInputDate();

  return (
    <div className="search-options">
      <div className="time-window-control">
        <CalendarDays size={17} />
        <div className="segmented-control" role="group" aria-label="Search window">
          {timeWindowChoices.map((choice) => (
            <button
              key={choice.mode}
              type="button"
              className={mode === choice.mode ? "active" : ""}
              aria-pressed={mode === choice.mode}
              disabled={disabled}
              onClick={() => onModeChange(choice.mode)}
            >
              {choice.label}
            </button>
          ))}
        </div>
        {mode === "custom" && (
          <div className="custom-range">
            <input
              type="date"
              value={customStartDate}
              max={customEndDate || today}
              disabled={disabled}
              aria-label="Custom start date"
              onChange={(event) => onStartDateChange(event.target.value)}
            />
            <span>to</span>
            <input
              type="date"
              value={customEndDate}
              min={customStartDate}
              max={today}
              disabled={disabled}
              aria-label="Custom end date"
              onChange={(event) => onEndDateChange(event.target.value)}
            />
          </div>
        )}
        {selectedWindow.error && (
          <span className="window-summary invalid">
            {selectedWindow.error}
          </span>
        )}
      </div>
    </div>
  );
}

function LeadCard({
  lead,
  selected,
  rated,
  onToggleSelected,
  onMark,
  onRate
}: {
  lead: Lead;
  selected: boolean;
  rated?: "up" | "down";
  onToggleSelected: (leadId: number, selected: boolean) => void;
  onMark: (lead: Lead, label: "buyer" | "not_buyer", reason: string) => Promise<void>;
  onRate: (lead: Lead, verdict: "up" | "down") => Promise<void>;
}) {
  return (
    <article className={`lead-card ${selected ? "selected" : ""}`}>
      <label className="lead-select" title="Select lead">
        <input
          type="checkbox"
          checked={selected}
          onChange={(event) => onToggleSelected(lead.id, event.target.checked)}
        />
      </label>
      <div className="lead-card-main">
        <div className="lead-card-title">
          <a href={lead.url} target="_blank" rel="noreferrer">
            {lead.sourceKind === "comment"
              ? `Comment in r/${lead.subreddit || "unknown"}`
              : lead.title || "Untitled opportunity"}
          </a>
          <span className={`grade grade-${(lead.leadGrade || "u").toLowerCase()}`}>
            {lead.leadGrade || "-"} · {lead.intentScore}
          </span>
        </div>
        <p>{lead.content || "No conversation text captured."}</p>
        {(lead.evidenceSnippet || lead.evidenceSummary) && (
          <div className="lead-proof">
            <span>Why this is a lead</span>
            {lead.evidenceSnippet && <blockquote>“{lead.evidenceSnippet}”</blockquote>}
            {lead.evidenceSummary && <p>{lead.evidenceSummary}</p>}
          </div>
        )}
        <div className="suggested-action">
          <span>Suggested next step</span>
          <strong>{lead.suggestedAction}</strong>
        </div>
        <div className="lead-meta">
          <span>r/{lead.subreddit || "unknown"}</span>
          <span>{lead.sourceKind === "comment" ? "comment" : "post"}</span>
          <span>{lead.author || "unknown author"}</span>
          <span>{formatPostedAt(lead.publishedAt, lead.postedAge)}</span>
          {lead.sourceKind !== "comment" && (
            <span>{lead.commentCount ?? 0} comments</span>
          )}
        </div>
      </div>

      <div className="intent-panel">
        <div>
          <span>Intent</span>
          <strong>{humanize(lead.intentLabel || lead.reasonFamily || "buyer intent")}</strong>
        </div>
        <div>
          <span>Confidence</span>
          <strong>
            {lead.verificationConfidence !== undefined
              ? `${Math.round(lead.verificationConfidence * 100)}% verified`
              : `${lead.intentScore}% model`}
          </strong>
        </div>
        <div>
          <span>Urgency</span>
          <strong>{humanize(lead.urgency || "unknown")} · {lead.urgencyScore ?? 20}</strong>
        </div>
        <div>
          <span>Market</span>
          <strong>{humanize(lead.niche || "unrouted")}</strong>
        </div>
        {lead.riskFlags?.length > 0 && (
          <div className="lead-risk">
            <span>Watch for</span>
            <strong>{lead.riskFlags.slice(0, 2).map(humanize).join(" · ")}</strong>
          </div>
        )}
      </div>

      <div className="lead-actions">
        <a className="button-link" href={lead.url} target="_blank" rel="noreferrer">
          <ExternalLink size={15} />
          Open
        </a>
        <button className="good" onClick={() => onMark(lead, "buyer", lead.reasonFamily || buyerReasons[0])}>
          <ThumbsUp size={16} />
          Qualified
        </button>
        {/* Global gold label — truth about INTENT ("this author is not a buyer of
            anything"), not topical taste. "Not relevant for my business" belongs to
            the Fewer-like-this button, which stays inside this tenant's workspace. */}
        <button
          className="bad"
          title="Marks this as not genuine buyer intent (trains lead detection). If it's a real buyer that just doesn't fit your business, use 'Fewer like this' instead."
          onClick={() => onMark(lead, "not_buyer", noiseReasons[0])}
        >
          <ThumbsDown size={16} />
          Not a buyer
        </button>
        <div className="lead-preference" title="Tune future results for this search — only affects your workspace">
          <button
            className={`pref ${rated === "up" ? "active" : ""}`}
            onClick={() => onRate(lead, "up")}
            aria-label="More like this"
          >
            <ThumbsUp size={13} />
            More like this
          </button>
          <button
            className={`pref ${rated === "down" ? "active" : ""}`}
            onClick={() => onRate(lead, "down")}
            aria-label="Fewer like this"
          >
            <ThumbsDown size={13} />
            Fewer like this
          </button>
        </div>
      </div>
    </article>
  );
}

function BillingNotice({
  billing,
  onCheckout,
  onPortal
}: {
  billing: BillingState | null;
  onCheckout: (plan: string) => void;
  onPortal: () => void;
}) {
  if (!billing?.stripeEnabled || billing.hasAccess) return null;

  const plans = billing.plans.filter((plan) => plan.configured);
  return (
    <section className="notice billing">
      <CreditCard size={18} />
      <span>{billing.message ?? "An active subscription is required."}</span>
      {billing.portalEnabled && <button onClick={onPortal}>Manage</button>}
      {billing.checkoutEnabled &&
        plans.map((plan) => (
          <button key={plan.name} className="primary" onClick={() => onCheckout(plan.name)}>
            {plan.displayName}
          </button>
        ))}
    </section>
  );
}

function AuthGate({
  googleEnabled,
  error,
  onDismissError
}: {
  googleEnabled: boolean;
  error: string | null;
  onDismissError: () => void;
}) {
  const returnUrl = `${window.location.pathname}${window.location.search}`;

  return (
    <main className="app-shell auth-shell">
      <section className="auth-panel">
        <div className="brand-lockup">
          <span className="brand-mark">DS</span>
          <span>DemandSift</span>
        </div>
        <div className="auth-copy">
          <p className="eyebrow">Private beta</p>
          <h1>DemandSift turns public conversations into qualified demand.</h1>
          <p>Find direct and indirect buying signals across communities, then export the opportunities most worth pursuing.</p>
        </div>

        {googleEnabled ? (
          <button className="primary auth-button" onClick={() => window.location.assign(api.googleLoginUrl(returnUrl))}>
            <LogIn size={17} />
            Continue with Google
          </button>
        ) : (
          <div className="auth-warning">
            Google sign-in is not configured for this environment.
          </div>
        )}
      </section>

      {error && (
        <section className="notice error">
          <CircleStop size={18} />
          <span>{shortError(error)}</span>
          <button onClick={onDismissError}>Dismiss</button>
        </section>
      )}
    </main>
  );
}

type SearchWindow = {
  daysBack: number;
  label: string;
  startDate?: string;
  endDate?: string;
  error?: string;
};

const maxSearchWindowDays = 90;
const oneDayMs = 24 * 60 * 60 * 1000;

function resolveTimeWindow(mode: TimeWindowMode, customStartDate: string, customEndDate: string): SearchWindow {
  if (mode !== "custom") {
    const choice = timeWindowChoices.find((item) => item.mode === mode) ?? timeWindowChoices[1];
    const endDate = todayInputDate();
    const startDate = shiftInputDate(endDate, 1 - choice.days);
    return {
      daysBack: choice.days,
      label: choice.label,
      startDate,
      endDate
    };
  }

  const start = parseInputDate(customStartDate);
  const end = parseInputDate(customEndDate);
  const today = parseInputDate(todayInputDate());
  if (!start || !end || !today) {
    return { daysBack: 7, label: "Custom", error: "Choose a valid custom date range." };
  }
  if (start.getTime() > end.getTime()) {
    return { daysBack: 7, label: "Custom", error: "Start date must be before end date." };
  }
  if (end.getTime() > today.getTime()) {
    return { daysBack: 7, label: "Custom", error: "End date cannot be in the future." };
  }

  const daysBack = Math.floor((end.getTime() - start.getTime()) / oneDayMs) + 1;
  if (daysBack > maxSearchWindowDays) {
    return {
      daysBack,
      label: "Custom",
      error: `Custom searches are limited to ${maxSearchWindowDays} days.`
    };
  }

  return {
    daysBack,
    label: `${formatInputDateLabel(customStartDate)} to ${formatInputDateLabel(customEndDate)}`,
    startDate: customStartDate,
    endDate: customEndDate
  };
}

function todayInputDate() {
  return new Date().toISOString().slice(0, 10);
}

function defaultCustomStartDate() {
  return shiftInputDate(todayInputDate(), -6);
}

function shiftInputDate(value: string, days: number) {
  const date = parseInputDate(value) ?? new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function parseInputDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatInputDateLabel(value: string) {
  const date = parseInputDate(value);
  if (!date) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  });
}

function readNumber(state: JobState | Record<string, unknown>, key: string) {
  const value = state[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function readStringArray(state: JobState | Record<string, unknown>, key: string) {
  const value = state[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim() !== "")
    : [];
}

function customerProgressLine(sourceDiscovery: JobState | null, liveFetch: JobState | null) {
  if (liveFetch?.isRunning) return "Scanning fresh conversations and filtering seller noise, off-topic posts, and low-intent chatter.";
  if (sourceDiscovery?.isRunning) return "Identifying communities where buyers ask for recommendations, alternatives, and implementation help.";
  return "Preparing the search.";
}

function formatPostedAt(value?: string, postedAge?: string) {
  if (!value) return "posted unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "posted unknown";

  const age = postedAge && postedAge.trim() ? postedAge : formatAgeFromDate(date);
  const exact = date.toISOString().slice(0, 16).replace("T", " ");
  return `${age} · ${exact} UTC`;
}

function formatAgeFromDate(date: Date) {
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ago`;
  if (hours > 0) return `${hours}h ${minutes}m ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "just now";
}

function humanize(value: string) {
  return value
    .replace(/^noise_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function shortError(error: string) {
  try {
    const parsed = JSON.parse(error) as { detail?: string; title?: string; error?: string };
    return parsed.detail || parsed.error || parsed.title || error;
  } catch {
    return error;
  }
}

type ExportRow = {
  Title: string;
  Url: string;
  Source: string;
  Type: string;
  Author: string;
  Posted: string;
  Comments: string;
  Score: string;
  Grade: string;
  Intent: string;
  Stage: string;
  Confidence: string;
  Urgency: string;
  Evidence: string;
  WhyThisIsALead: string;
  SuggestedAction: string;
  RiskFlags: string;
  Category: string;
  Content: string;
};

function exportRowsCsv(leads: Lead[], query: string) {
  const rows = exportRows(leads);
  const headers = Object.keys(rows[0] ?? {}) as (keyof ExportRow)[];
  const csv = [
    headers.map(csvCell).join(","),
    ...rows.map((row) => headers.map((header) => csvCell(row[header])).join(","))
  ].join("\n");
  downloadBlob(csv, "text/csv;charset=utf-8", exportFilename(query, "csv"));
}

function exportRowsXls(leads: Lead[], query: string) {
  const rows = exportRows(leads);
  const headers = Object.keys(rows[0] ?? {}) as (keyof ExportRow)[];
  const table = `
    <html>
      <head><meta charset="utf-8" /></head>
      <body>
        <table>
          <thead><tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("")}</tr></thead>
          <tbody>
            ${rows
              .map((row) => `<tr>${headers.map((header) => `<td>${escapeHtml(row[header])}</td>`).join("")}</tr>`)
              .join("")}
          </tbody>
        </table>
      </body>
    </html>`;
  downloadBlob(table, "application/vnd.ms-excel;charset=utf-8", exportFilename(query, "xls"));
}

function exportRowsPdf(leads: Lead[], query: string) {
  const rows = exportRows(leads);
  const title = query ? `DemandSift Opportunities - ${query}` : "DemandSift Opportunities";
  const win = window.open("", "_blank", "width=1024,height=768");
  if (!win) return;
  win.opener = null;

  win.document.write(`
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: Inter, Arial, sans-serif; color: #17202a; margin: 28px; }
          h1 { margin: 0 0 6px; font-size: 22px; }
          .meta { color: #657184; margin-bottom: 18px; }
          article { border: 1px solid #d8dee8; border-radius: 8px; padding: 14px; margin-bottom: 12px; break-inside: avoid; }
          h2 { margin: 0 0 8px; font-size: 16px; }
          p { line-height: 1.45; }
          .chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0; }
          .chips span { border: 1px solid #d8dee8; border-radius: 6px; padding: 4px 7px; color: #3b4655; font-size: 12px; }
          a { color: #14566e; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <div class="meta">${rows.length} selected opportunit${rows.length === 1 ? "y" : "ies"} · ${new Date().toISOString().slice(0, 16).replace("T", " ")} UTC</div>
        ${rows
          .map(
            (row) => `
              <article>
                <h2>${escapeHtml(row.Title)}</h2>
                <a href="${escapeAttribute(row.Url)}">${escapeHtml(row.Url)}</a>
                <p>${escapeHtml(row.Content)}</p>
                <div class="chips">
                  <span>${escapeHtml(row.Source)}</span>
                  <span>${escapeHtml(row.Type)}</span>
                  <span>${escapeHtml(row.Posted)}</span>
                  <span>${escapeHtml(row.Comments)} comments</span>
                  <span>Grade ${escapeHtml(row.Grade)} · ${escapeHtml(row.Score)}</span>
                  <span>${escapeHtml(row.Intent)}</span>
                  <span>${escapeHtml(row.Stage)}</span>
                  <span>${escapeHtml(row.Confidence)}</span>
                  <span>${escapeHtml(row.Urgency)}</span>
                </div>
                ${row.Evidence ? `<p><strong>Evidence:</strong> “${escapeHtml(row.Evidence)}”</p>` : ""}
                ${row.WhyThisIsALead ? `<p><strong>Why this is a lead:</strong> ${escapeHtml(row.WhyThisIsALead)}</p>` : ""}
                <p><strong>Suggested next step:</strong> ${escapeHtml(row.SuggestedAction)}</p>
              </article>`
          )
          .join("")}
      </body>
    </html>
  `);
  win.document.close();
  win.focus();
  win.print();
}

function exportRows(leads: Lead[]): ExportRow[] {
  return leads.map((lead) => ({
    Title: lead.title || "Untitled opportunity",
    Url: lead.url,
    Source: lead.subreddit ? `r/${lead.subreddit}` : "unknown",
    Type: lead.sourceKind === "comment" ? "comment" : "post",
    Author: lead.author || "unknown author",
    Posted: formatPostedAt(lead.publishedAt, lead.postedAge),
    Comments: String(lead.commentCount ?? 0),
    Score: String(lead.intentScore),
    Grade: lead.leadGrade || "",
    Intent: humanize(lead.intentLabel || lead.reasonFamily || "buyer intent"),
    Stage: humanize(lead.buyerStage || lead.triggerEvent || "unclear"),
    Confidence: lead.verificationConfidence !== undefined
      ? `${Math.round(lead.verificationConfidence * 100)}% verified`
      : `${lead.intentScore}% model`,
    Urgency: `${humanize(lead.urgency || "unknown")} (${lead.urgencyScore ?? 20})`,
    Evidence: lead.evidenceSnippet || "",
    WhyThisIsALead: lead.evidenceSummary || "",
    SuggestedAction: lead.suggestedAction || "",
    RiskFlags: (lead.riskFlags || []).map(humanize).join("; "),
    Category: humanize(lead.niche || "unrouted"),
    Content: lead.content || "",
  }));
}

function downloadBlob(content: string, type: string, filename: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function formatOperatorTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "unknown"
    : date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function exportFilename(query: string, extension: string) {
  const stem = normalize(query || "leads").replace(/\s+/g, "-") || "leads";
  const timestamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
  return `socialmonitor-${stem}-${timestamp}.${extension}`;
}

function csvCell(value: string) {
  return `"${value.replace(/\r?\n/g, " ").replace(/"/g, '""')}"`;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttribute(value: string) {
  return escapeHtml(value).replace(/'/g, "&#39;");
}

function bestProfileForQuery(query: string, profiles: NicheProfile[]) {
  const normalizedQuery = normalize(query);
  const queryTokens = tokenSet(normalizedQuery);
  let best: { profile: NicheProfile; score: number } | null = null;

  for (const profile of profiles) {
    const weightedPhrases = [
      profile.name,
      profile.displayName,
      profile.targetBuyer,
      profile.offerDescription,
      ...profile.keywords,
      ...profile.subreddits
    ];
    let score = 0;

    for (const phrase of weightedPhrases) {
      const normalizedPhrase = normalize(phrase);
      if (!normalizedPhrase) continue;
      if (normalizedQuery.includes(normalizedPhrase) || normalizedPhrase.includes(normalizedQuery)) {
        score += phrase === profile.name || phrase === profile.displayName ? 8 : 4;
      }
      for (const token of tokenSet(normalizedPhrase)) {
        if (queryTokens.has(token)) score += 1;
      }
    }

    if (!best || score > best.score) best = { profile, score };
  }

  return best && best.score >= 2 ? best.profile : null;
}

function normalize(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function tokenSet(value: string) {
  return new Set(
    value
      .split(" ")
      .map((token) => token.trim())
      .filter((token) => token.length >= 3 && !profileStopwords.has(token))
  );
}

const profileStopwords = new Set([
  "best",
  "for",
  "lead",
  "leads",
  "need",
  "service",
  "services",
  "software",
  "tool",
  "tools",
  "what",
  "with",
  "would",
  "you",
  "like"
]);
